import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
    Box,
    Typography,
    IconButton,
    Badge,
    Popover,
    Avatar,
    Button,
} from "@mui/material";
import {
    NotificationsNone as BellIcon,
    Favorite as LikeIcon,
    ChatBubble as CommentIcon,
    StickyNote2 as NoteBellIcon,
} from "@mui/icons-material";
import firebase from "../../config/fbConfig";

const GOLD = "#c9a24b";

const tsToMoment = (ts, isFr) => {
    const d = ts?.toDate ? ts.toDate() : null;
    if (!d) return "";
    return moment(d).locale(isFr ? "fr" : "en").fromNow();
};

const initials = (name) =>
    (name || "?").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const MastermindNotifications = ({ auth, isFr, color = "white" }) => {
    const [items, setItems] = useState([]);
    const [anchor, setAnchor] = useState(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        if (!auth?.uid) return undefined;
        // Equality-only query → no composite index; sort client-side.
        const unsub = firebase
            .firestore()
            .collection("mastermindNotifications")
            .where("userId", "==", auth.uid)
            .limit(40)
            .onSnapshot(
                (snap) => {
                    if (!mounted.current) return;
                    const list = snap.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
                    setItems(list);
                },
                (err) => console.error("[MastermindNotifications] listen failed", err)
            );
        return () => {
            mounted.current = false;
            unsub();
        };
    }, [auth?.uid]);

    const unread = items.filter((n) => !n.read).length;

    const markAllRead = async () => {
        const toMark = items.filter((n) => !n.read);
        if (toMark.length === 0) return;
        const batch = firebase.firestore().batch();
        toMark.forEach((n) => batch.update(firebase.firestore().collection("mastermindNotifications").doc(n.id), { read: true }));
        try {
            await batch.commit();
        } catch (e) {
            console.error("[MastermindNotifications] markAllRead failed", e);
        }
    };

    const open = (e) => setAnchor(e.currentTarget);
    const close = () => setAnchor(null);

    const label = (n) => {
        if (n.type === "like") return isFr ? `${n.fromName} a aimé ta publication` : `${n.fromName} liked your post`;
        if (n.type === "session_note") return isFr ? `${n.fromName} a partagé une note de séance` : `${n.fromName} shared a session note`;
        if (n.type === "event") return isFr ? `${n.fromName} a planifié un événement` : `${n.fromName} scheduled an event`;
        return isFr ? `${n.fromName} a commenté ta publication` : `${n.fromName} commented on your post`;
    };

    return (
        <>
            <IconButton onClick={open} sx={{ color }}>
                <Badge badgeContent={unread} color="error">
                    <BellIcon />
                </Badge>
            </IconButton>
            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: { width: 360, maxWidth: "90vw", bgcolor: "#0f172a", color: "white", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 3 } }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
                    <Typography fontWeight={800}>{isFr ? "Notifications" : "Notifications"}</Typography>
                    {unread > 0 && (
                        <Button onClick={markAllRead} size="small" sx={{ textTransform: "none", color: GOLD, fontWeight: 700 }}>
                            {isFr ? "Tout marquer lu" : "Mark all read"}
                        </Button>
                    )}
                </Box>
                <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                    {items.length === 0 ? (
                        <Box textAlign="center" py={4} sx={{ color: "rgba(255,255,255,0.4)" }}>
                            <Typography variant="body2">{isFr ? "Aucune notification" : "No notifications"}</Typography>
                        </Box>
                    ) : (
                        items.map((n) => (
                            <Box
                                key={n.id}
                                display="flex"
                                gap={1.5}
                                px={2}
                                py={1.25}
                                sx={{ bgcolor: n.read ? "transparent" : "rgba(251,191,36,0.08)", borderTop: "1px solid rgba(255,255,255,0.05)" }}
                            >
                                <Box position="relative">
                                    <Avatar src={n.fromAvatar || undefined} sx={{ width: 36, height: 36, bgcolor: "#334155", fontSize: 13 }}>
                                        {!n.fromAvatar && initials(n.fromName)}
                                    </Avatar>
                                    <Box sx={{ position: "absolute", bottom: -2, right: -2, width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: n.type === "like" ? "#f43f5e" : (n.type === "session_note" || n.type === "event") ? "#8a6d1f" : "#6366f1" }}>
                                        {n.type === "like" ? <LikeIcon sx={{ fontSize: 10, color: "white" }} /> : (n.type === "session_note" || n.type === "event") ? <NoteBellIcon sx={{ fontSize: 10, color: "white" }} /> : <CommentIcon sx={{ fontSize: 10, color: "white" }} />}
                                    </Box>
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="body2" lineHeight={1.3}>{label(n)}</Typography>
                                    {n.excerpt && (
                                        <Typography variant="caption" color="rgba(255,255,255,0.45)" sx={{ display: "block" }}>
                                            “{n.excerpt}”
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="rgba(255,255,255,0.35)">
                                        {tsToMoment(n.createdAt, isFr)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                </Box>
            </Popover>
        </>
    );
};

export default MastermindNotifications;
